import {
  createBrowserRouter,
  createMemoryRouter,
  Navigate,
  RouterProvider,
  Outlet,
  type RouteObject,
} from 'react-router-dom';
import { AppFrame } from './components/layout/AppFrame';
import { RequireAuth } from './components/auth/RequireAuth';
import { RequireAdmin } from './components/auth/RequireAdmin';
import { RequireMandantAccessForCase } from './components/auth/RequireMandantAccessForCase';
import { Login } from './routes/Login';
import { Settings as AdminSettings } from './routes/admin/Settings';
import { AuthCallback } from './routes/AuthCallback';
import { AuthReset } from './routes/AuthReset';
import { Cases } from './routes/Cases';
import { NewCase } from './routes/NewCase';
import { Cockpit } from './routes/Cockpit';
import { BeraterDimensionRoute } from './routes/BeraterDimensionRoute';
import { Datenschutz } from './routes/Datenschutz';
import { Impressum } from './routes/Impressum';
import { ProfileForm } from './routes/ProfileForm';
import { CaseReview } from './routes/CaseReview';
import { NotFound } from './routes/NotFound';
import { CaseHub } from './components/hub/CaseHub';
import { TriageStep } from './routes/wizard/TriageStep';
import { IdentityStep } from './routes/wizard/IdentityStep';
import { ActivityStep } from './routes/wizard/ActivityStep';
import { StyleStep } from './routes/wizard/StyleStep';
import { ResourcesStep } from './routes/wizard/ResourcesStep';
import { HealthStep } from './routes/wizard/HealthStep';
import { PrivateStep } from './routes/wizard/PrivateStep';
import { StammdatenStep } from './routes/wizard/StammdatenStep';
import { MandantRedeem } from './routes/MandantRedeem';
import { MandantCaseHome } from './routes/MandantCaseHome';
import { MandantOverview } from './routes/MandantOverview';
import { MandantWizardWrapper } from './routes/MandantWizardWrapper';
import { BeraterWizardWrapper } from './routes/BeraterWizardWrapper';
import { MandantDsgvoSelfService } from './routes/MandantDsgvoSelfService';
import { MandantPrintView } from './routes/MandantPrintView';
import { ConfirmDialogProvider } from './components/ui/ConfirmDialog';
import { ToastProvider } from './components/ui/ToastProvider';

/**
 * Wrapper, der den AppFrame um jede Route legt. Wir nutzen <Outlet/> als
 * Render-Slot, damit das Frame nicht pro Route dupliziert werden muss.
 */
function FramedLayout() {
  return (
    <AppFrame>
      <Outlet />
    </AppFrame>
  );
}

const wizardRoutes: RouteObject[] = [
  // Modul 00 — Stammdaten (Mandant-Self-Erfassung, Phase A)
  { path: 'stammdaten', element: <StammdatenStep /> },
  { path: 'triage', element: <TriageStep /> },
  { path: 'identity', element: <IdentityStep /> },
  { path: 'activity', element: <ActivityStep /> },
  { path: 'style', element: <StyleStep /> },
  { path: 'resources', element: <ResourcesStep /> },
  { path: 'health', element: <HealthStep /> },
  { path: 'private', element: <PrivateStep /> },
  { index: true, element: <Navigate to="triage" replace /> },
];

/** Zentrale Route-Definition. Wird sowohl vom Browser-Router (Produktion) als
 *  auch vom Memory-Router (Tests) konsumiert. */
export const routes: RouteObject[] = [
  // Print-View bewusst OHNE FramedLayout (eigene Druck-CSS, sauberes Layout
  // ohne Header/Footer-Navigation).
  {
    path: '/mandant/case/:caseId/print',
    element: (
      <RequireAuth>
        <RequireMandantAccessForCase>
          <MandantPrintView />
        </RequireMandantAccessForCase>
      </RequireAuth>
    ),
  },
  {
    element: <FramedLayout />,
    children: [
      { path: '/', element: <Navigate to="/login" replace /> },
      { path: '/login', element: <Login /> },
      { path: '/auth/callback', element: <AuthCallback /> },
      { path: '/auth/reset', element: <AuthReset /> },
      // Public-Routes (Pflicht: ohne Login erreichbar)
      { path: '/datenschutz', element: <Datenschutz /> },
      { path: '/impressum', element: <Impressum /> },
      {
        path: '/cases',
        element: (
          <RequireAuth>
            <Cases />
          </RequireAuth>
        ),
      },
      {
        path: '/profile',
        element: (
          <RequireAuth>
            <ProfileForm />
          </RequireAuth>
        ),
      },
      {
        path: '/admin/settings',
        element: (
          <RequireAuth>
            <RequireAdmin>
              <AdminSettings />
            </RequireAdmin>
          </RequireAuth>
        ),
      },
      {
        path: '/case/new',
        element: (
          <RequireAuth>
            <NewCase />
          </RequireAuth>
        ),
      },
      {
        path: '/case/:id/hub',
        element: (
          <RequireAuth>
            <CaseHub />
          </RequireAuth>
        ),
      },
      {
        path: '/case/:id/wizard',
        element: (
          <RequireAuth>
            <BeraterWizardWrapper>
              <Outlet />
            </BeraterWizardWrapper>
          </RequireAuth>
        ),
        children: wizardRoutes,
      },
      {
        path: '/case/:id/cockpit',
        element: (
          <RequireAuth>
            <Cockpit />
          </RequireAuth>
        ),
      },
      {
        path: '/case/:id/review',
        element: (
          <RequireAuth>
            <CaseReview />
          </RequireAuth>
        ),
      },
      {
        path: '/case/:id/dimension/:dim',
        element: (
          <RequireAuth>
            <BeraterDimensionRoute />
          </RequireAuth>
        ),
      },
      // Mandanten-Self-Service-Pfad — eigene Routen mit eigenem Guard.
      // Hinweis: Wir verwenden bewusst denselben URL-Param-Namen `:id`
      // wie im Berater-Pfad, damit die existierenden Wizard-Step-Komponenten
      // (TriageStep, IdentityStep, …) ohne Änderung weiterfunktionieren.
      // Die Mandant-spezifische Verhaltensweise (andere Navigation, andere
      // Texte) fließt über den <WizardAudienceProvider> in die Komponenten.
      {
        path: '/mandant/redeem',
        element: <MandantRedeem />,
      },
      {
        path: '/mandant/case/:caseId',
        element: (
          <RequireAuth>
            <RequireMandantAccessForCase>
              <MandantCaseHome />
            </RequireMandantAccessForCase>
          </RequireAuth>
        ),
      },
      {
        path: '/mandant/case/:caseId/uebersicht',
        element: (
          <RequireAuth>
            <RequireMandantAccessForCase>
              <MandantOverview />
            </RequireMandantAccessForCase>
          </RequireAuth>
        ),
      },
      {
        path: '/mandant/case/:caseId/datenschutz',
        element: (
          <RequireAuth>
            <RequireMandantAccessForCase>
              <MandantDsgvoSelfService />
            </RequireMandantAccessForCase>
          </RequireAuth>
        ),
      },
      {
        path: '/mandant/case/:id/wizard',
        element: (
          <RequireAuth>
            <RequireMandantAccessForCase>
              <MandantWizardWrapper>
                <Outlet />
              </MandantWizardWrapper>
            </RequireMandantAccessForCase>
          </RequireAuth>
        ),
        children: wizardRoutes,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
];

const browserRouter = createBrowserRouter(routes);

export function App() {
  return (
    <ToastProvider>
      <ConfirmDialogProvider>
        <RouterProvider router={browserRouter} />
      </ConfirmDialogProvider>
    </ToastProvider>
  );
}

/**
 * Test-Helper: erstellt einen Memory-Router mit den Produktions-Routes,
 * damit Tests mit `initialEntries` arbeiten können, ohne dass der globale
 * Browser-Router mit jsdom-Inkompatibilitäten kollidiert.
 */
export function createTestApp(initialEntries: string[] = ['/login']) {
  const router = createMemoryRouter(routes, { initialEntries });
  return (
    <ToastProvider>
      <ConfirmDialogProvider>
        <RouterProvider router={router} />
      </ConfirmDialogProvider>
    </ToastProvider>
  );
}
